import { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  Alert,
  Platform,
  Pressable,
  Modal,
  Keyboard,
  KeyboardAvoidingView,
  ScrollView,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { useFocusEffect, useLocalSearchParams, useRouter } from 'expo-router';
import { useIsFocused } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { Screen } from '@/components/layout/Screen';
import { Card } from '@/components/ui/Card';
import { SelectField, type SelectOption } from '@/components/ui/SelectField';
import { Body, Caption, ThemedText } from '@/components/ui/Text';
import { useTheme } from '@/theme';
import { useToast } from '@/components/ui/ToastProvider';
import { TripErrorBoundary } from '@/utils/errorBoundary';
import { LoadingState } from '@/components/ui/LoadingState';
import { useUserStore } from '@/state/userStore';
import { useTripSelectionStore } from '@/state/tripSelectionStore';
import { subscribeToUserTrips, updateTrip, type Trip, DEFAULT_BUDGET_ALERT_THRESHOLDS, getLastKnownTrips } from '@/services/trips';
import { logger } from '@/services/logger';
import { canUserEditItem } from '@/services/collaboration';
import type { CollaboratorPermission } from '@/services/trips';
import {
  checkInboxStatus,
  initiateInboxAuth,
  saveImapSettings,
  disconnectEmailProvider,
  type CheckInboxStatusOptions,
  type InboxProviderId,
} from '@/services/inboxSync';
import { useInboxConnectionsStore } from '@/state/inboxConnectionsStore';
import { subscribeToTripBookings, type Booking } from '@/services/bookings';
import {
  subscribeToTripExpenses,
  fetchTripExpenses,
  createExpense,
  updateExpense,
  deleteExpense,
  type Expense,
} from '@/services/expenses';
import { subscribeToTripItineraryItems, type ItineraryItem } from '@/services/itinerary';
import { ExportMenu } from '@/components/trips/ExportMenu';
// NOTE: Expense/split notifications to collaborators require Cloud Functions with FCM
// Local notifications can only go to the current device, which would notify the actor
// about their own action - confusing and incorrect behavior.
// Deferred post-launch: implement Cloud Functions for expense/split FCM notifications.
// Tracking: docs/PRELAUNCH_DEFERRED_ITEMS.md#notifications-cross-device-fcm
import { useLocalTripBudgetSummary } from '@/hooks/useTripBudgetSummaries';
import { buildTripSelectOptions } from '@/utils/tripSelectOptions';
import { getDefaultTripId, normalizeTripRouteParam } from '@/utils/tripSelection';
import { hasPremiumAccess } from '@/services/entitlements';
import { ExpenseForm } from '@/components/expenses/ExpenseForm';
import { CollaborationModal } from '@/components/trips/CollaborationModal';
import { WalletTabBar } from '@/components/wallet/WalletTabBar';
import { FinancialTab } from '@/components/wallet/FinancialTab';
import { AnalyticsTab } from '@/components/wallet/AnalyticsTab';
import { TripQuickStats } from '@/components/expenses/TripQuickStats';
import * as Linking from 'expo-linking';
import * as WebBrowser from 'expo-web-browser';
import { ImapSettingsModal } from '@/components/planning/ImapSettingsModal';
import { EmailSyncStatus } from '@/components/planning/EmailSyncStatus';
import { MultiProviderSelector } from '@/components/planning/MultiProviderSelector';
import { isTripCompleted } from '@/utils/tripStatus';
import { formatCurrency } from '@/utils/currency';
import { hapticError, hapticLight, hapticWarning } from '@/utils/haptics';
import { completeOAuthWebSession } from '@/utils/completeOAuthWebSession';
import { InputField } from '@/components/ui/InputField';
import { SubHeading } from '@/components/ui/Text';
import { Button } from '@/components/ui/Button';
import { getLastUsedInboxProvider, setLastUsedInboxProvider } from '@/utils/lastInboxProvider';
import { setInboxOauthPending } from '@/utils/inboxOauth';
import Constants from 'expo-constants';
import { CURRENCY_OPTIONS } from '@/data/profileOptions';
import { NumberStepper } from '@/components/ui/NumberStepper';
import type { BudgetCategoryAllocations, BudgetCategoryKey } from '@/types/budget';
import { normalizeBudgetCategoryAllocations } from '@/utils/budgetAllocations';
import { coerceDate } from '@/utils/dateKeys';
import { effectiveTopInset } from '@/utils/effectiveTopInset';
import { useInboxOauthResume } from '@/hooks/useInboxOauthResume';
import { useInboxOauthPostMessage } from '@/hooks/useInboxOauthPostMessage';
import {
  getCachedTrips,
  getCachedTripData,
  cacheTripData,
  itineraryItemsFromOfflineCache,
  expensesFromOfflineCache,
  mergeOfflineReceiptUrisIntoExpenses,
  bookingsFromOfflineCache,
} from '@/services/offlineTrips';
import { networkMonitor } from '@/services/networkMonitor';
import { traceModuleEvaluation } from '@/utils/moduleTrace';

traceModuleEvaluation('app/(tabs)/wallet');

const PROVIDER_LABELS: Record<InboxProviderId, string> = {
  gmail: 'Gmail',
  outlook: 'Outlook',
  icloud: 'iCloud',
};

const WALLET_TAB_STORAGE_KEY = 'wallet:lastTab';

function filterCachedTripExpenses(expenses: Expense[], tripId: string): Expense[] {
  return expenses.filter((e) => !e.tripId || e.tripId === tripId);
}

/** Immediately reflect trip writes in Wallet UI; subscribeToUserTrips can lag after setDoc/offline-queue. */
function mergeTripIntoListById(tripsList: Trip[], tripId: string, patch: Partial<Trip>): Trip[] {
  if (!tripsList.some((t) => t.id === tripId)) return tripsList;
  return tripsList.map((t) => (t.id === tripId ? { ...t, ...patch } : t));
}

function WalletScreenInner() {
  const theme = useTheme();
  const params = useLocalSearchParams();
  const paramTripId = normalizeTripRouteParam(params.tripId);
  const shouldOpenExpenseForm = params.fileExpense === 'true';
  const router = useRouter();
  const firebaseUser = useUserStore((state) => state.firebaseUser);
  const { confirm: confirmAction } = useConfirmDialog();
  const { showSuccess, showError, showInfo, showToastWithOptions } = useToast();
  const profile = useUserStore((state) => state.profile);
  const walletOfflinePackPremium = hasPremiumAccess({
    isPremium: profile?.isPremium,
    planTier: profile?.planTier,
    entitlement: profile?.entitlement,
  });
  const isWalletFocused = useIsFocused();

  const [trips, setTrips] = useState<Trip[]>([]);
  const [tripsLoading, setTripsLoading] = useState(true);
  const rememberedTripId = useTripSelectionStore((state) => state.lastViewedTripId);
  const rememberTripId = useTripSelectionStore((state) => state.rememberTripId);
  const [selectedTripId, setSelectedTripId] = useState<string | null>(paramTripId ?? rememberedTripId ?? null);
  const [inboxProvider, setInboxProvider] = useState<InboxProviderId>('gmail');
  const [inboxSyncing, setInboxSyncing] = useState(false);
  const [showEmailSyncStatus, setShowEmailSyncStatus] = useState(false);
  const inboxConnectionsFromStore = useInboxConnectionsStore((s) => s.connections);
  const inboxConnectionsLoading = useInboxConnectionsStore((s) => s.loading);
  const inboxConnections: Record<InboxProviderId, boolean | null> = useMemo(() => {
    if (!firebaseUser?.uid) {
      return { gmail: null, outlook: null, icloud: null };
    }
    if (inboxConnectionsLoading) {
      return { gmail: null, outlook: null, icloud: null };
    }
    return {
      gmail: inboxConnectionsFromStore.gmail,
      outlook: inboxConnectionsFromStore.outlook,
      icloud: inboxConnectionsFromStore.icloud,
    };
  }, [firebaseUser?.uid, inboxConnectionsLoading, inboxConnectionsFromStore]);
  const [imapModalVisible, setImapModalVisible] = useState(false);
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [oauthListenerActive, setOauthListenerActive] = useState(false);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [itineraryItems, setItineraryItems] = useState<ItineraryItem[]>([]);
  const [showCollaborationModal, setShowCollaborationModal] = useState(false);
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [activeTab, setActiveTab] = useState<'financial' | 'analytics'>('financial');
  const [showBudgetEditor, setShowBudgetEditor] = useState(false);
  const [costConfidenceDismissedIds, setCostConfidenceDismissedIds] = useState<Set<string>>(new Set());
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [lastBudgetAlert, setLastBudgetAlert] = useState<number | null>(null);
  const previousBookingCountRef = useRef(0);
  const previousBookingTotalRef = useRef(0);
  const onlineExpenseEmptySinceRef = useRef<number | null>(null);
  const onlineExpenseEmptyVerifyInFlightRef = useRef(false);
  const fileExpenseHandledTripRef = useRef<string | null>(null);

  // Persist last selected tab for user continuity
  useEffect(() => {
    AsyncStorage.getItem(WALLET_TAB_STORAGE_KEY)
      .then((savedTab) => {
        if (savedTab === 'financial' || savedTab === 'analytics') {
          setActiveTab(savedTab);
        }
      })
      .catch(() => {
        // Silently ignore persistence errors
      });
  }, []);

  useEffect(() => {
    AsyncStorage.setItem(WALLET_TAB_STORAGE_KEY, activeTab).catch(() => {
      // Ignore persistence errors to avoid blocking UI
    });
  }, [activeTab]);

  const refreshInboxStatus = useCallback(
    async (provider: InboxProviderId, opts?: CheckInboxStatusOptions) => {
      if (!firebaseUser?.uid) {
        return null;
      }

      try {
        const status = await checkInboxStatus(provider, opts);
        useInboxConnectionsStore.getState().setProviderConnected(provider, status.connected ?? false);
        return status;
      } catch (error) {
        console.error(`[Wallet] Error checking ${provider} inbox`, error);
        useInboxConnectionsStore.getState().setProviderConnected(provider, false);
        return null;
      }
    },
    [firebaseUser?.uid],
  );

  /** Prefer last-used inbox when the global snapshot updates (same Trips/Profile source of truth). */
  useEffect(() => {
    if (!firebaseUser?.uid) return;
    const uid = firebaseUser.uid;
    let cancelled = false;
    const order: InboxProviderId[] = ['gmail', 'outlook', 'icloud'];
    const firstConnected = order.find((p) => inboxConnectionsFromStore[p]) ?? null;
    void getLastUsedInboxProvider(uid).then((last) => {
      if (cancelled) return;
      if (last && inboxConnectionsFromStore[last]) {
        setInboxProvider(last);
      } else if (firstConnected) {
        setInboxProvider(firstConnected);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [firebaseUser?.uid, inboxConnectionsFromStore]);

  useEffect(() => {
    const shouldCheck =
      showProviderModal ||
      showEmailSyncStatus ||
      imapModalVisible;
    if (!shouldCheck) return;
    refreshInboxStatus(inboxProvider, { bypassCache: true });
  }, [refreshInboxStatus, inboxProvider, showProviderModal, showEmailSyncStatus, imapModalVisible]);

  const handleOauthConnected = useCallback(
    async (provider: InboxProviderId) => {
      setInboxProvider(provider);
      showInfo('Connected, starting sync...');
      setShowEmailSyncStatus(true);
    },
    [showInfo],
  );

  const oauthProjectId =
    (Constants.expoConfig?.extra as any)?.firebaseConfig?.projectId ||
    process.env.EXPO_PUBLIC_FIREBASE_PROJECT_ID;

  useFocusEffect(
    useCallback(() => {
      setOauthListenerActive(true);
      return () => {
        setOauthListenerActive(false);
      };
    }, []),
  );

  useInboxOauthResume({
    userId: firebaseUser?.uid,
    enabled: Boolean(selectedTripId) && oauthListenerActive,
    canResume: () => Boolean(selectedTripId),
    checkStatus: (p) => refreshInboxStatus(p, { bypassCache: true }),
    onConnected: handleOauthConnected,
  });

  useInboxOauthPostMessage({
    userId: firebaseUser?.uid,
    enabled: Boolean(selectedTripId) && oauthListenerActive,
    canResume: () => Boolean(selectedTripId),
    projectId: typeof oauthProjectId === 'string' ? oauthProjectId : undefined,
    checkStatus: (p) => refreshInboxStatus(p, { bypassCache: true }),
    onConnected: handleOauthConnected,
  });

  // Subscribe to trips updates.
  // Gate on `isWalletFocused` so we unsubscribe while the tab is blurred —
  // otherwise the native Firestore listener keeps firing callbacks into the
  // JS thread even while the tab is frozen, contending with other tabs on
  // resume and causing the post-Wallet navigation lag.
  useEffect(() => {
    if (!firebaseUser?.uid) {
      setTrips([]);
      setTripsLoading(false);
      return;
    }
    if (!isWalletFocused) return;

    setTripsLoading(true);
    let cancelled = false;

    const lastKnown = getLastKnownTrips(firebaseUser.uid);
    if (lastKnown?.trips?.length) {
      setTrips(lastKnown.trips);
      setTripsLoading(false);
    }

    getCachedTrips(firebaseUser.uid)
      .then((cached) => {
        if (cancelled) return;
        if (Array.isArray(cached) && cached.length > 0) {
          setTrips((prev) => (prev.length > 0 ? prev : cached));
          setTripsLoading(false);
        }
      })
      .catch((error) => {
        logger.warn(
          '[WalletScreen] Failed to read cached trips',
          undefined,
          error instanceof Error ? error : new Error(String(error)),
        );
      });

    const unsubscribe = subscribeToUserTrips(firebaseUser.uid, (userTrips) => {
      setTrips((prev) => {
        if (
          networkMonitor.shouldUseOfflineDataFallback() &&
          prev.length > 0 &&
          userTrips.length === 0
        ) {
          return prev;
        }
        return userTrips;
      });
      setTripsLoading(false);
    });

    return () => {
      cancelled = true;
      if (unsubscribe) {
        try {
          unsubscribe();
        } catch (error) {
          // Silently ignore cleanup errors
        }
      }
    };
  }, [firebaseUser?.uid, isWalletFocused]);

  // Initialize selected trip
  useEffect(() => {
    if (trips.length === 0 || tripsLoading) return;

    if (paramTripId && trips.some((t) => t.id === paramTripId)) {
      if (selectedTripId !== paramTripId) {
        setSelectedTripId(paramTripId);
      }
      if (paramTripId !== selectedTripId) {
        router.setParams({ tripId: paramTripId });
      }
      if (shouldOpenExpenseForm && fileExpenseHandledTripRef.current !== paramTripId) {
        setEditingExpense(null);
        setShowExpenseForm(true);
        fileExpenseHandledTripRef.current = paramTripId;
        router.setParams({ fileExpense: undefined });
      }
      return;
    }

    const defaultTripId =
      (rememberedTripId && trips.some((trip) => trip.id === rememberedTripId)
        ? rememberedTripId
        : getDefaultTripId(trips, firebaseUser?.uid));
    if (defaultTripId && defaultTripId !== selectedTripId) {
      setSelectedTripId(defaultTripId);
      if (paramTripId !== defaultTripId) {
        router.setParams({ tripId: defaultTripId });
      }
    }
  }, [paramTripId, shouldOpenExpenseForm, trips, selectedTripId, firebaseUser?.uid, router, tripsLoading, rememberedTripId]);

  // Get selected trip
  const selectedTrip = useMemo(() => {
    return trips.find((trip) => trip.id === selectedTripId) || null;
  }, [selectedTripId, trips]);

  useEffect(() => {
    if (selectedTripId) {
      rememberTripId(selectedTripId);
    }
  }, [rememberTripId, selectedTripId]);

  // Clear financial lists when the active trip changes so we never show (or cache offline) the
  // previous trip's bookings/expenses while the new Firestore listeners warm up. The transient-empty
  // expense debounce below would otherwise keep stale rows when switching to an empty-wallet trip.
  useEffect(() => {
    setBookings([]);
    setExpenses([]);
    setItineraryItems([]);
    onlineExpenseEmptySinceRef.current = null;
    onlineExpenseEmptyVerifyInFlightRef.current = false;
  }, [selectedTripId]);

  const walletPackTripRef = useRef(selectedTrip);
  walletPackTripRef.current = selectedTrip;
  const walletPackExpensesRef = useRef(expenses);
  walletPackExpensesRef.current = expenses;
  const walletPackItineraryRef = useRef(itineraryItems);
  walletPackItineraryRef.current = itineraryItems;
  const walletPackBookingsRef = useRef(bookings);
  walletPackBookingsRef.current = bookings;

  useEffect(() => {
    if (!selectedTrip) return;
    const start = coerceDate(selectedTrip.startDate);
    const end = coerceDate(selectedTrip.endDate);
    if (!start || !end) {
      console.warn('[Wallet] Invalid trip dates detected', {
        tripId: selectedTrip.id,
        startDate: selectedTrip.startDate,
        endDate: selectedTrip.endDate,
      });
    }
  }, [selectedTrip]);

  useEffect(() => {
    setLastBudgetAlert(null);
  }, [selectedTripId]);

  const isSelectedTripCompleted = selectedTrip ? isTripCompleted(selectedTrip) : false;
  const isSelectedTripReadOnly = selectedTrip ? (selectedTrip.isArchived || isSelectedTripCompleted) : false;

  // Get user's permission for selected trip
  const userPermission = useMemo((): CollaboratorPermission | 'owner' | null => {
    if (!selectedTrip || !firebaseUser?.uid) return null;
    if (selectedTrip.owner === firebaseUser.uid) return 'owner';
    const collaborator = selectedTrip.collaborators?.find(c => c.userId === firebaseUser.uid);
    return collaborator?.permission || null;
  }, [selectedTrip, firebaseUser?.uid]);

  const isOwner = userPermission === 'owner';

  // Compute the selected trip's budget snapshot locally from the expenses /
  // bookings this screen already subscribes to — avoids opening a duplicate
  // pair of Firestore streams via `useTripBudgetSummaries`. Pauses while the
  // tab is blurred so currency-conversion work does not compete with the
  // newly focused tab.
  const budgetSnapshot = useLocalTripBudgetSummary(
    selectedTrip,
    expenses,
    bookings,
    !!firebaseUser?.uid && !!selectedTrip && !tripsLoading && isWalletFocused,
  );

  useEffect(() => {
    if (!selectedTrip || !selectedTrip.budget || !budgetSnapshot) return;

    const totalSpent = (budgetSnapshot.expensesConverted || 0) + (budgetSnapshot.bookingsConverted || 0);
    if (totalSpent <= 0) return;

    const pctUsed = (totalSpent / selectedTrip.budget) * 100;
    const thresholds = (selectedTrip.budgetAlertThresholds && selectedTrip.budgetAlertThresholds.length > 0
      ? [...selectedTrip.budgetAlertThresholds]
      : DEFAULT_BUDGET_ALERT_THRESHOLDS
    ).sort((a, b) => a - b);

    const hit = thresholds.filter((t) => pctUsed >= t).pop() ?? null;

      if (hit && hit !== lastBudgetAlert) {
      setLastBudgetAlert(hit);

      if (hit >= 100) {
        showToastWithOptions({
          type: 'error',
          message: 'Over budget',
          details: 'You have exceeded your set budget.',
        });
        hapticError();
      } else if (hit >= 90) {
        showToastWithOptions({
          type: 'error',
          message: 'Nearing your budget',
          details: 'Spend carefully to avoid overages.',
        });
        hapticWarning();
      } else if (hit >= 75) {
        showSuccess(`Budget check-in — you have used ${Math.round(pctUsed)}% of your budget.`);
        hapticLight();
      }
    }
  }, [budgetSnapshot, lastBudgetAlert, selectedTrip, showSuccess, showToastWithOptions]);

  // Subscribe to bookings for selected trip.
  // Gate on `isWalletFocused` so we unsubscribe while the tab is blurred —
  // avoids background snapshot processing competing with the focused tab.
  useEffect(() => {
    if (!selectedTripId) {
      setBookings([]);
      return;
    }
    if (!isWalletFocused) return;

    setBookings([]);

    let cancelled = false;
    void getCachedTripData(selectedTripId).then((cached) => {
      if (cancelled || !cached?.bookings?.length) return;
      const fromCache = bookingsFromOfflineCache(cached.bookings as unknown[]);
      if (fromCache.length === 0) return;
      setBookings(fromCache);
    });

    const unsubscribe = subscribeToTripBookings(selectedTripId, (tripBookings) => {
      setBookings((prev) => {
        if (
          networkMonitor.shouldUseOfflineDataFallback() &&
          prev.length > 0 &&
          tripBookings.length === 0 &&
          prev.every((b) => b.tripId === selectedTripId)
        ) {
          return prev;
        }
        return tripBookings;
      });
    });

    return () => {
      cancelled = true;
      if (unsubscribe) {
        try {
          unsubscribe();
        } catch (error) {
          // Silently ignore cleanup errors
        }
      }
    };
  }, [selectedTripId, isWalletFocused]);

  // Notify when new bookings with amounts arrive (costs auto-applied)
  useEffect(() => {
    if (!selectedTrip) {
      previousBookingCountRef.current = 0;
      previousBookingTotalRef.current = 0;
      return;
    }

    const baseCurrency = selectedTrip.baseCurrency || 'USD';
    const totalWithAmounts = bookings.reduce((sum, booking) => {
      if (typeof booking.amount === 'number' && booking.currency === baseCurrency) {
        return sum + booking.amount;
      }
      return sum;
    }, 0);

    const prevCount = previousBookingCountRef.current;
    const prevTotal = previousBookingTotalRef.current;

    if (bookings.length > prevCount && totalWithAmounts > prevTotal) {
      showSuccess('New bookings synced—costs applied to your budget');
    }

    previousBookingCountRef.current = bookings.length;
    previousBookingTotalRef.current = totalWithAmounts;
  }, [bookings, selectedTrip, showSuccess]);

  // Subscribe to expenses for selected trip.
  // Gate on `isWalletFocused` so we unsubscribe while the tab is blurred and
  // skip the post-snapshot AsyncStorage/merge work that otherwise competes
  // with the newly focused tab's JS thread.
  useEffect(() => {
    if (!selectedTripId) {
      setExpenses([]);
      return;
    }
    if (!isWalletFocused) return;

    let cancelled = false;
    void getCachedTripData(selectedTripId).then((cached) => {
      if (cancelled || !cached?.expenses?.length) return;
      const fromCache = filterCachedTripExpenses(
        expensesFromOfflineCache(cached.expenses as unknown[]),
        selectedTripId,
      );
      if (fromCache.length === 0) return;
      setExpenses(fromCache);
    });

    const unsubscribe = subscribeToTripExpenses(selectedTripId, (tripExpenses) => {
      setExpenses((prev) => {
        if (tripExpenses.length > 0) {
          onlineExpenseEmptySinceRef.current = null;
        }
        if (
          networkMonitor.shouldUseOfflineDataFallback() &&
          prev.length > 0 &&
          tripExpenses.length === 0 &&
          prev.every((e) => e.tripId === selectedTripId)
        ) {
          return prev;
        }
        // Firestore can emit transient empty snapshots during reconnect; avoid flashing wallet to $0 immediately.
        if (
          networkMonitor.isOnline() &&
          prev.length > 0 &&
          tripExpenses.length === 0 &&
          prev.every((e) => e.tripId === selectedTripId)
        ) {
          const now = Date.now();
          const firstEmptyAt = onlineExpenseEmptySinceRef.current;
          if (firstEmptyAt == null) {
            onlineExpenseEmptySinceRef.current = now;
            return prev;
          }
          if (now - firstEmptyAt < 3500) {
            return prev;
          }
          if (!onlineExpenseEmptyVerifyInFlightRef.current) {
            onlineExpenseEmptyVerifyInFlightRef.current = true;
            void fetchTripExpenses(selectedTripId)
              .then((confirmed) => {
                if (cancelled) return;
                if (confirmed.length > 0) {
                  setExpenses(confirmed);
                  onlineExpenseEmptySinceRef.current = null;
                } else {
                  setExpenses([]);
                }
              })
              .catch(() => {
                // Keep previous state on verification failure.
              })
              .finally(() => {
                onlineExpenseEmptyVerifyInFlightRef.current = false;
              });
          }
          return prev;
        }
        return tripExpenses;
      });
      void getCachedTripData(selectedTripId).then((cached) => {
        if (cancelled || !cached?.expenses?.length) return;
        const fromCache = filterCachedTripExpenses(
          expensesFromOfflineCache(cached.expenses as unknown[]),
          selectedTripId,
        );
        setExpenses((cur) => mergeOfflineReceiptUrisIntoExpenses(cur, fromCache));
      });
    });

    return () => {
      cancelled = true;
      if (unsubscribe) {
        try {
          unsubscribe();
        } catch (error) {
          // Silently ignore cleanup errors
        }
      }
    };
  }, [selectedTripId, isWalletFocused]);

  // Subscribe to itinerary items for selected trip.
  // Gate on `isWalletFocused` so we unsubscribe while the tab is blurred.
  useEffect(() => {
    if (!selectedTripId) {
      setItineraryItems([]);
      return;
    }
    if (!isWalletFocused) return;

    setItineraryItems([]);

    let cancelled = false;
    void getCachedTripData(selectedTripId).then((cached) => {
      if (cancelled || !cached?.itinerary?.length) return;
      const fromCache = itineraryItemsFromOfflineCache(cached.itinerary, selectedTripId);
      if (fromCache.length === 0) return;
      setItineraryItems(fromCache);
    });

    const unsubscribe = subscribeToTripItineraryItems(selectedTripId, (items) => {
      setItineraryItems((prev) => {
        if (
          networkMonitor.shouldUseOfflineDataFallback() &&
          prev.length > 0 &&
          items.length === 0 &&
          prev.every((i) => i.tripId === selectedTripId)
        ) {
          return prev;
        }
        return items;
      });
    });

    return () => {
      cancelled = true;
      if (unsubscribe) {
        try {
          unsubscribe();
        } catch (error) {
          // Silently ignore cleanup errors
        }
      }
    };
  }, [selectedTripId, isWalletFocused]);

  // Merge trip pack on Wallet while online (expenses + itinerary + bookings) for offline use.
  useEffect(() => {
    if (!selectedTrip?.id || !networkMonitor.isOnline() || !isWalletFocused) return;
    if (!walletOfflinePackPremium) return;
    const timeoutId = setTimeout(() => {
      void (async () => {
        const trip = walletPackTripRef.current;
        if (!trip?.id || !networkMonitor.isOnline()) return;
        const expensesPack = walletPackExpensesRef.current;
        const bookingsPack = walletPackBookingsRef.current;
        const itineraryPack = walletPackItineraryRef.current;
        const rowTripMismatch =
          [...expensesPack, ...bookingsPack].some(
            (row) => row?.tripId && row.tripId !== trip.id,
          ) ||
          (Array.isArray(itineraryPack) &&
            itineraryPack.some((row: unknown) =>
              !!(row &&
                typeof row === 'object' &&
                'tripId' in row &&
                typeof (row as { tripId?: string }).tripId === 'string' &&
                (row as { tripId: string }).tripId !== trip.id),
            ));
        if (rowTripMismatch) {
          return;
        }
        await cacheTripData(trip, {
          expenses: walletPackExpensesRef.current,
          itinerary: walletPackItineraryRef.current,
          bookings: walletPackBookingsRef.current,
        });
        const cached = await getCachedTripData(trip.id);
        if (!cached?.expenses?.length) return;
        const fromCache = filterCachedTripExpenses(
          expensesFromOfflineCache(cached.expenses as unknown[]),
          trip.id,
        );
        setExpenses((cur) => mergeOfflineReceiptUrisIntoExpenses(cur, fromCache));
      })();
    }, 5000);
    return () => clearTimeout(timeoutId);
  }, [selectedTrip, expenses, itineraryItems, bookings, isWalletFocused, walletOfflinePackPremium]);

  const tripOptions = useMemo<SelectOption[]>(() => buildTripSelectOptions(trips), [trips]);

  const handleTripChange = (tripId: string) => {
    setSelectedTripId(tripId);
    rememberTripId(tripId);
    router.setParams({ tripId });
  };

  const handleWalletTabChange = useCallback((tab: 'financial' | 'analytics') => {
    setActiveTab(tab);
  }, []);

  const handleInboxSync = useCallback(async () => {
    if (!firebaseUser?.uid || !selectedTripId) {
      Alert.alert('Select a trip', 'Please select a trip before syncing your inbox.');
      return;
    }

    setInboxSyncing(true);
    let popup: { close: () => void; location: { href: string } } | null = null;
    try {
      const status = await refreshInboxStatus(inboxProvider);

      if (inboxProvider === 'icloud' && (!status || !status.connected)) {
        setInboxSyncing(false);
        setImapModalVisible(true);
        return;
      }

      if (status?.requiresAuth || !status?.connected) {
        await setLastUsedInboxProvider(firebaseUser.uid, inboxProvider).catch(() => {});
        await setInboxOauthPending(inboxProvider).catch(() => {});

        // Open OAuth only when auth is needed (avoid blank-popup flash when callables/error paths run first).
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          try {
            popup = window.open('about:blank', '_blank', 'width=600,height=700') as {
              close: () => void;
              location: { href: string };
            } | null;
          } catch {
            popup = null;
          }
        }

        const authResponse = await initiateInboxAuth(inboxProvider);
        if (authResponse?.authUrl) {
          if (Platform.OS === 'web') {
            if (popup) {
              try {
                popup.location.href = authResponse.authUrl;
              } catch {
                window.location.href = authResponse.authUrl;
              }
            } else {
              window.location.href = authResponse.authUrl;
            }
            Alert.alert('Continue in browser', authResponse.message || 'Complete the connection flow and return to Tripvio.');
          } else {
            try {
              popup?.close();
            } catch {
              /* ignore */
            }
            const canOpen = await Linking.canOpenURL(authResponse.authUrl);
            if (canOpen) {
              const redirectUrl = Linking.createURL(
                inboxProvider === 'gmail' ? 'gmail-callback' : 'outlook-callback',
              );
              const result = await WebBrowser.openAuthSessionAsync(authResponse.authUrl, redirectUrl);
              completeOAuthWebSession();
              if (result.type !== 'success') {
                Alert.alert('Authentication cancelled', 'Return to Tripvio and try connecting again.');
                setInboxSyncing(false);
                return;
              }
            } else {
              Alert.alert('Connect inbox', 'Unable to open the authorization page automatically.');
              setInboxSyncing(false);
              return;
            }
          }
        } else {
          try {
            popup?.close();
          } catch {
            /* ignore */
          }
          Alert.alert('Connect inbox', authResponse?.message || 'Unable to start authorization for this provider.');
        }
        setInboxSyncing(false);
        return;
      }

      setShowEmailSyncStatus(true);
      setInboxSyncing(false);
    } catch (error) {
      try {
        popup?.close();
      } catch {
        /* ignore */
      }
      console.error('Inbox sync error:', error);
      Alert.alert('Inbox sync failed', error instanceof Error ? error.message : 'Failed to sync inbox. Please try again.');
      setInboxSyncing(false);
    }
  }, [firebaseUser?.uid, selectedTripId, inboxProvider, refreshInboxStatus]);

  const handleImapSave = useCallback(
    async (settings: {
      host: string;
      username: string;
      password: string;
      port: number;
      secure: boolean;
      mailbox: string;
    }) => {
      try {
        await saveImapSettings({
          ...settings,
          providerId: 'icloud',
        });
        setImapModalVisible(false);
        await refreshInboxStatus('icloud');
        Alert.alert('Mailbox connected', 'Your iCloud/IMAP credentials were saved.');
        handleInboxSync();
      } catch (error) {
        console.error('[Wallet] Failed to save IMAP settings', error);
        Alert.alert('Unable to save credentials', error instanceof Error ? error.message : 'Please double-check your mailbox details and try again.');
      }
    },
    [handleInboxSync, refreshInboxStatus],
  );

  const handleChangeProvider = useCallback((provider?: InboxProviderId) => {
    if (provider) {
      setInboxProvider(provider);
    }
    setShowProviderModal(true);
  }, []);

  const handleConnectProvider = useCallback(async (provider: InboxProviderId) => {
    if (!firebaseUser?.uid || !selectedTripId) {
      Alert.alert('Select a trip', 'Please select a trip before connecting email.');
      return;
    }

    let popup: { close: () => void; location: { href: string } } | null = null;
    try {
      const status = await refreshInboxStatus(provider);

      await setLastUsedInboxProvider(firebaseUser.uid, provider).catch(() => {});
      
      if (provider === 'icloud' && (!status || !status.connected)) {
        setImapModalVisible(true);
        return;
      }

      if (status?.requiresAuth || !status?.connected) {
        await setInboxOauthPending(provider).catch(() => {});

        if (Platform.OS === 'web' && typeof window !== 'undefined' && (provider === 'gmail' || provider === 'outlook')) {
          try {
            popup = window.open('about:blank', '_blank', 'width=600,height=700') as {
              close: () => void;
              location: { href: string };
            } | null;
          } catch {
            popup = null;
          }
        }

        const authResponse = await initiateInboxAuth(provider);
        if (authResponse?.authUrl) {
          if (Platform.OS === 'web') {
            if (popup) {
              try {
                popup.location.href = authResponse.authUrl;
              } catch {
                window.location.href = authResponse.authUrl;
              }
            } else {
              window.location.href = authResponse.authUrl;
            }
            Alert.alert('Continue in browser', authResponse.message || 'Complete the connection flow and return to Tripvio.');
          } else {
            try {
              popup?.close();
            } catch {
              /* ignore */
            }
            const canOpen = await Linking.canOpenURL(authResponse.authUrl);
            if (canOpen) {
              const redirectUrl = Linking.createURL(
                provider === 'gmail' ? 'gmail-callback' : 'outlook-callback',
              );
              const result = await WebBrowser.openAuthSessionAsync(authResponse.authUrl, redirectUrl);
              completeOAuthWebSession();
              if (result.type !== 'success') {
                Alert.alert('Authentication cancelled', 'Return to Tripvio and try connecting again.');
                return;
              }
            } else {
              Alert.alert('Connect inbox', 'Unable to open the authorization page automatically.');
            }
          }
        } else {
          try {
            popup?.close();
          } catch {
            /* ignore */
          }
          Alert.alert('Connect inbox', authResponse?.message || 'Unable to start authorization for this provider.');
        }
      }
    } catch (error) {
      try {
        popup?.close();
      } catch {
        /* ignore */
      }
      console.error('[WalletScreen] Error connecting provider:', error);
      Alert.alert('Error', 'Unable to connect email provider. Please try again.');
    }
  }, [firebaseUser?.uid, selectedTripId, refreshInboxStatus]);

  const handleSelectProvider = useCallback((provider: InboxProviderId) => {
    setInboxProvider(provider);
    setShowProviderModal(false);
    if (firebaseUser?.uid) {
      setLastUsedInboxProvider(firebaseUser.uid, provider).catch(() => {});
    }
  }, [firebaseUser?.uid]);

  const handleDisconnectProvider = useCallback(
    async (provider: InboxProviderId) => {
      if (!firebaseUser?.uid) return;
      try {
        const ok = await disconnectEmailProvider(provider);
        if (!ok) {
          throw new Error('Disconnect operation returned unsuccessful response');
        }
        await useInboxConnectionsStore.getState().refreshFresh();
        if (inboxProvider === provider) {
          const conn = useInboxConnectionsStore.getState().connections;
          const fallback =
            (['gmail', 'outlook', 'icloud'] as InboxProviderId[]).find(
              (p) => p !== provider && conn[p],
            ) ?? 'gmail';
          setInboxProvider(fallback);
        }
        showSuccess(`${PROVIDER_LABELS[provider]} disconnected`);
      } catch (error) {
        console.error('[WalletScreen] Error disconnecting provider:', error);
        showError('Failed to disconnect email provider. Please try again.');
        throw error;
      }
    },
    [firebaseUser?.uid, inboxProvider, showSuccess, showError],
  );

  const handleSyncAll = useCallback(async () => {
    if (!firebaseUser?.uid || !selectedTripId) return;

    const providers: InboxProviderId[] = ['gmail', 'outlook', 'icloud'];
    const connectedProviders: InboxProviderId[] = [];

    for (const provider of providers) {
      const status = await refreshInboxStatus(provider);
      if (status?.connected) {
        connectedProviders.push(provider);
      }
    }

    if (connectedProviders.length === 0) {
      Alert.alert('No providers connected', 'Please connect at least one email provider first.');
      return;
    }

    setShowEmailSyncStatus(true);
  }, [firebaseUser?.uid, selectedTripId, refreshInboxStatus]);

  // Handle expense operations
  const handleAddExpense = async (expense: Omit<Expense, 'id' | 'tripId' | 'createdAt' | 'updatedAt'>) => {
    if (!selectedTripId || !firebaseUser?.uid) {
      throw new Error('Trip ID or user ID is missing');
    }
    const optimisticId = `optimistic-expense-${Date.now()}`;
    const optimisticExpense: Expense = {
      ...expense,
      id: optimisticId,
      tripId: selectedTripId,
      createdBy: expense.createdBy ?? firebaseUser.uid,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    setExpenses((prev) =>
      [optimisticExpense, ...prev].sort((a, b) => b.date.getTime() - a.date.getTime()),
    );
    try {
      await createExpense(selectedTripId, {
        ...expense,
        updatedBy: firebaseUser.uid,
      });
      setShowExpenseForm(false);
      setEditingExpense(null);
    } catch (error) {
      setExpenses((prev) => prev.filter((item) => item.id !== optimisticId));
      console.error('[Wallet] Error adding expense:', error);
      throw error;
    }
  };

  const handleEditExpense = async (expense: Expense, updates: Partial<Expense>) => {
    if (!selectedTripId || !selectedTrip || !firebaseUser?.uid) return;
    const previous = expenses.find((item) => item.id === expense.id);
    setExpenses((prev) =>
      prev
        .map((item) =>
          item.id === expense.id ? { ...item, ...updates, updatedAt: new Date() } : item,
        )
        .sort((a, b) => b.date.getTime() - a.date.getTime()),
    );
    try {
      await updateExpense(selectedTripId, expense.id, {
        ...updates,
        updatedBy: firebaseUser.uid,
      });
      setShowExpenseForm(false);
      setEditingExpense(null);

      showSuccess('Expense updated successfully');
    } catch (error) {
      if (previous) {
        setExpenses((prev) =>
          prev.map((item) => (item.id === expense.id ? previous : item)),
        );
      } else {
        void fetchTripExpenses(selectedTripId).then(setExpenses).catch(() => undefined);
      }
      console.error('[Wallet] Error updating expense:', error);
      throw error;
    }
  };

  const handleDeleteExpense = async (expense: Expense) => {
    if (!selectedTripId) {
      Alert.alert('Error', 'No trip selected. Please select a trip first.');
      return;
    }

    confirmAction({
      title: 'Delete Expense',
      message: `Are you sure you want to delete "${expense.description || 'this expense'}"?`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: async () => {
        const previous = expenses;
        setExpenses((prev) => prev.filter((item) => item.id !== expense.id));
        try {
          await deleteExpense(selectedTripId, expense.id);
        } catch (error: any) {
          setExpenses(previous);
          console.error('[Wallet] Error deleting expense:', error);
          const errorMessage = error?.message || 'Failed to delete expense. Please try again.';
          Alert.alert('Error', errorMessage);
        }
      },
    });
  };

  const handleSaveBudget = async (
    budget: number | null,
    currency: string,
    thresholds: number[],
    categoryAllocations?: BudgetCategoryAllocations,
  ) => {
    if (!selectedTripId || !selectedTrip || !firebaseUser?.uid) return;

    try {
      await updateTrip(selectedTripId, firebaseUser.uid, {
        budget: budget,
        baseCurrency: currency,
        budgetAlertThresholds: thresholds,
        budgetCategoryAllocations: categoryAllocations,
      });
      const budgetPatch: Partial<Trip> = {
        budget,
        baseCurrency: currency,
        budgetAlertThresholds: thresholds,
      };
      if (categoryAllocations !== undefined) {
        budgetPatch.budgetCategoryAllocations = categoryAllocations;
      }
      setTrips((prev) => mergeTripIntoListById(prev, selectedTripId, budgetPatch));
    } catch (error) {
      console.error('[Wallet] Error saving budget:', error);
      throw error;
    }
  };

  const handleApplyBudgetFromEstimate = async (amount: number) => {
    if (!selectedTrip) return;
    const currency = selectedTrip.baseCurrency || 'USD';
    const thresholds =
      selectedTrip.budgetAlertThresholds && selectedTrip.budgetAlertThresholds.length > 0
        ? [...selectedTrip.budgetAlertThresholds]
        : DEFAULT_BUDGET_ALERT_THRESHOLDS;
    await handleSaveBudget(amount, currency, thresholds);
    showSuccess('Trip budget set from your estimate');
  };

  const handleSaveCategoryAllocations = async (allocations: BudgetCategoryAllocations) => {
    if (!selectedTripId || !selectedTrip || !firebaseUser?.uid) return;
    try {
      await updateTrip(selectedTripId, firebaseUser.uid, {
        budgetCategoryAllocations: allocations,
      });
      setTrips((prev) =>
        mergeTripIntoListById(prev, selectedTripId, {
          budgetCategoryAllocations: allocations,
        }),
      );
      showSuccess('Category allocation updated');
    } catch (error) {
      console.error('[Wallet] Error saving category allocations:', error);
      showError('Failed to update category allocation. Please try again.');
      throw error;
    }
  };

  const showDropdown = trips.length > 1;

  if (tripsLoading) {
    return (
      <Screen>
        <LoadingState message="Loading trips..." />
      </Screen>
    );
  }

  if (trips.length === 0) {
    return (
      <Screen
        title="Wallet"
        subtitle="Track budgets, expenses, and receipts in one place."
        scrollable={true}
      >
        <Card>
          <View style={{ alignItems: 'center', paddingVertical: theme.spacing(6), gap: theme.spacing(2) }}>
            <View
              style={{
                width: 80,
                height: 80,
                borderRadius: 40,
                backgroundColor: theme.colors.primary + '10',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: theme.spacing(1),
              }}
            >
              <Ionicons name="wallet-outline" size={40} color={theme.colors.primary} />
            </View>
            <ThemedText variant="bodyStrong" style={{ fontSize: 20, textAlign: 'center', fontWeight: '700' }}>
              No trips yet
            </ThemedText>
            <Body color={theme.colors.textSecondary} style={{ textAlign: 'center', fontSize: 15, lineHeight: 22, maxWidth: 280 }}>
              Create your first trip to start planning and tracking expenses.
            </Body>
            <Button
              label="Go to Trips"
              variant="primary"
              onPress={() => router.push('/(tabs)')}
              icon={<Ionicons name="arrow-forward" size={18} color={theme.colorScheme === 'dark' ? '#000000' : '#FFFFFF'} />}
              style={{ marginTop: theme.spacing(2), minWidth: 200 }}
              accessibilityLabel="Go to trips tab"
              accessibilityHint="Navigate to the trips tab to create your first trip"
            />
          </View>
        </Card>
      </Screen>
    );
  }

  const headerActions = selectedTrip && firebaseUser?.uid ? (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
      <Pressable
        onPress={() => setShowBudgetEditor(true)}
        style={({ pressed }) => ({
          flexDirection: 'row',
          alignItems: 'center',
          gap: theme.spacing(0.5),
          paddingHorizontal: theme.spacing(1.25),
          paddingVertical: theme.spacing(0.75),
          borderRadius: theme.radii.md,
          backgroundColor: selectedTrip.budget
            ? theme.colors.primary + '18'
            : theme.colors.borderMuted + '40',
          opacity: pressed ? 0.7 : 1,
          minHeight: 44,
          minWidth: 44,
        })}
        accessibilityRole="button"
        accessibilityLabel={selectedTrip.budget ? "Edit budget" : "Set budget"}
        accessibilityHint={selectedTrip.budget 
          ? `Current budget is ${formatCurrency(selectedTrip.budget, selectedTrip.baseCurrency || 'USD')}. Tap to edit.`
          : "Set your trip budget and currency"}
      >
        <Ionicons
          name={selectedTrip.budget ? 'wallet' : 'wallet-outline'}
          size={18}
          color={theme.colors.primary}
        />
        {selectedTrip.budget && (
          <ThemedText
            variant="caption"
            style={{
              color: theme.colors.primary,
              fontSize: 12,
              fontWeight: '600',
            }}
          >
            {formatCurrency(selectedTrip.budget, selectedTrip.baseCurrency || 'USD')}
          </ThemedText>
        )}
      </Pressable>
      <Pressable
        onPress={() => setShowExportMenu(true)}
        style={({ pressed }) => ({
          padding: theme.spacing(0.75),
          borderRadius: theme.radii.md,
          opacity: pressed ? 0.7 : 1,
          minHeight: 44,
          minWidth: 44,
          alignItems: 'center',
          justifyContent: 'center',
        })}
        accessibilityRole="button"
        accessibilityLabel="Export trip"
        accessibilityHint="Export your trip data, itinerary, or expenses"
      >
        <Ionicons
          name="download-outline"
          size={22}
          color={theme.colors.primary}
        />
      </Pressable>
      <Pressable
        onPress={() => setShowCollaborationModal(true)}
        style={({ pressed }) => ({
          padding: theme.spacing(0.75),
          borderRadius: theme.radii.md,
          opacity: pressed ? 0.7 : 1,
          minHeight: 44,
          minWidth: 44,
          alignItems: 'center',
          justifyContent: 'center',
        })}
        accessibilityRole="button"
        accessibilityLabel="Manage collaborators"
        accessibilityHint="Add or remove people who can view or edit this trip"
      >
        <Ionicons name="people-outline" size={22} color={theme.colors.primary} />
      </Pressable>
    </View>
  ) : null;

  return (
    <Screen
      title="Wallet"
      subtitle="Track budgets, expenses, and receipts in one place."
      headerAction={headerActions}
      scrollable={true}
    >
      {selectedTrip ? (
        <>
          {/* Trip Selection and Info Section */}
          <Card variant="elevated" elevation={1}>
            <View style={{ gap: theme.spacing(2.5) }}>
              {showDropdown && (
                <>
                  <View style={{ 
                    flexDirection: 'row', 
                    alignItems: 'center', 
                    gap: theme.spacing(0.75),
                    marginBottom: theme.spacing(0.5),
                  }}>
                    <Ionicons name="wallet-outline" size={14} color={theme.colors.textSecondary} />
                    <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
                      Viewing wallet for:
                    </Caption>
                  </View>
                  <SelectField
                    label="Select trip"
                    value={selectedTripId || undefined}
                    options={tripOptions}
                    onValueChange={(value) => {
                      handleTripChange(value);
                    }}
                    placeholder="Choose a trip"
                  />
                </>
              )}
              
              <View style={{ marginTop: showDropdown ? theme.spacing(1) : 0 }}>
                <TripQuickStats trip={selectedTrip} noCard />
              </View>
              
              {isSelectedTripReadOnly && (
                <Card variant="outlined" elevation={0} style={{
                  backgroundColor: theme.colors.borderMuted + '20',
                  borderWidth: 1,
                  borderColor: theme.colors.borderMuted,
                }}>
                  <View style={{ 
                    flexDirection: 'row', 
                    alignItems: 'flex-start', 
                    gap: theme.spacing(1.5),
                    padding: theme.spacing(1.5),
                  }}>
                    <View
                      style={{
                        padding: theme.spacing(1),
                        borderRadius: theme.radii.md,
                        backgroundColor: theme.colors.textSecondary + '20',
                      }}
                    >
                      <Ionicons
                        name={selectedTrip?.isArchived ? 'archive' : 'lock-closed'}
                        size={20}
                        color={theme.colors.textSecondary}
                      />
                    </View>
                    <View style={{ flex: 1, gap: theme.spacing(0.5) }}>
                      <ThemedText variant="bodyStrong" style={{ fontSize: 14 }}>
                        {selectedTrip?.isArchived ? 'Trip archived' : 'Trip completed'}
                      </ThemedText>
                      <Body color={theme.colors.textSecondary} style={{ fontSize: 12 }}>
                        {selectedTrip?.isArchived
                          ? 'This trip is in read-only mode. Unarchive it from the Trips tab to make edits.'
                          : 'This trip has finished and is locked. Reopen it from the Trips tab if you need to make changes.'}
                      </Body>
                    </View>
                  </View>
                </Card>
              )}
            </View>
          </Card>

          {/* Tab Navigation */}
          <View style={{ zIndex: 100 }}>
            <WalletTabBar 
              activeTab={activeTab} 
              onTabChange={handleWalletTabChange}
            />
          </View>

          {/* Tab Content */}
          {activeTab === 'financial' ? (
            <TripErrorBoundary name="FinancialTab">
              <FinancialTab
                trip={selectedTrip}
                bookings={bookings}
                expenses={expenses}
                isScreenFocused={isWalletFocused}
                isArchived={isSelectedTripReadOnly}
                canEdit={
                  !isSelectedTripReadOnly &&
                  (isOwner || userPermission === 'edit' || userPermission === 'add_items')
                }
                userId={firebaseUser?.uid || ''}
                userPermission={userPermission}
                isOwner={isOwner}
                budgetSnapshot={budgetSnapshot}
                onSaveCategoryAllocations={handleSaveCategoryAllocations}
                onEditExpense={(expense) => {
                  if (canUserEditItem(firebaseUser?.uid || '', expense.createdBy, userPermission, isOwner)) {
                    setEditingExpense(expense);
                    setShowExpenseForm(true);
                  } else {
                    Alert.alert('Permission Denied', 'You can only edit expenses that you created.');
                  }
                }}
                onDeleteExpense={handleDeleteExpense}
                onAddExpense={() => {
                  setEditingExpense(null);
                  setShowExpenseForm(true);
                }}
                onSyncInbox={handleInboxSync}
                inboxSyncing={inboxSyncing}
                inboxConnected={inboxConnections[inboxProvider] ?? null}
                inboxProviderLabel={PROVIDER_LABELS[inboxProvider]}
                onChangeInboxProvider={handleChangeProvider}
                onReceiptProcessed={(expense) => {
                  if (expense.status === 'parsed' || expense.status === 'failed') {
                    setEditingExpense(expense);
                    setShowExpenseForm(true);
                  }
                }}
                onOpenBudgetEditor={() => setShowBudgetEditor(true)}
                itineraryItems={itineraryItems}
                onOpenExportMenu={() => setShowExportMenu(true)}
                profile={profile}
                costConfidenceDismissed={
                  selectedTripId ? costConfidenceDismissedIds.has(selectedTripId) : false
                }
                onDismissCostConfidence={() => {
                  if (!selectedTripId) return;
                  setCostConfidenceDismissedIds((prev) => new Set(prev).add(selectedTripId));
                }}
                onApplyBudgetFromEstimate={handleApplyBudgetFromEstimate}
              />
            </TripErrorBoundary>
          ) : (
            <TripErrorBoundary name="AnalyticsTab">
              <AnalyticsTab
                trip={selectedTrip}
                expenses={expenses}
                bookings={bookings}
              />
            </TripErrorBoundary>
          )}

          {/* Add Expense is handled by floating FAB (Screen.floating) */}
        </>
      ) : (
        <Card>
          <View style={{ alignItems: 'center', paddingVertical: theme.spacing(6), gap: theme.spacing(2) }}>
            <View style={{
              width: 80,
              height: 80,
              borderRadius: 40,
              backgroundColor: theme.colors.primary + '10',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: theme.spacing(1),
            }}>
              <Ionicons name="wallet-outline" size={40} color={theme.colors.primary} />
            </View>
            <ThemedText variant="bodyStrong" style={{ fontSize: 20, textAlign: 'center', fontWeight: '700' }}>
              No trip selected
            </ThemedText>
            {tripOptions.length > 0 ? (
              <>
                <Body
                  color={theme.colors.textSecondary}
                  style={{ textAlign: 'center', fontSize: 15, lineHeight: 22, maxWidth: 320 }}
                >
                  Choose a trip to view its wallet and manage expenses.
                </Body>
                <View style={{ width: '100%', maxWidth: 420 }}>
                  <SelectField
                    label="Select trip"
                    value={selectedTripId || undefined}
                    options={tripOptions}
                    onValueChange={(value) => handleTripChange(value)}
                    placeholder="Choose a trip"
                  />
                </View>
              </>
            ) : (
              <Body
                color={theme.colors.textSecondary}
                style={{ textAlign: 'center', fontSize: 15, lineHeight: 22, maxWidth: 320 }}
              >
                Create a trip from the Trips tab to start tracking expenses.
              </Body>
            )}
          </View>
        </Card>
      )}

      {selectedTrip && firebaseUser?.uid && (
        <>
          <CollaborationModal
            visible={showCollaborationModal}
            onClose={() => setShowCollaborationModal(false)}
            trip={selectedTrip}
            currentUserId={firebaseUser.uid}
            onCollaboratorsUpdated={() => {
              // Trips will automatically update via subscription
            }}
            onLeftTrip={() => setSelectedTripId(null)}
          />

          <ImapSettingsModal
            visible={imapModalVisible}
            onClose={() => setImapModalVisible(false)}
            onSave={handleImapSave}
          />

          <TripErrorBoundary name="EmailSyncStatus">
            <EmailSyncStatus
              visible={showEmailSyncStatus}
              onClose={() => setShowEmailSyncStatus(false)}
              tripId={selectedTripId!}
              provider={inboxProvider}
              syncSource="wallet"
              onSyncComplete={(bookings) => {
                // Keep modal open so the success screen can offer "Sync another email".
                if (bookings.length > 0) {
                  showSuccess(`Imported ${bookings.length} booking${bookings.length === 1 ? '' : 's'} from ${inboxProvider.toUpperCase()}.`);
                } else {
                  showSuccess(`No new bookings found in ${inboxProvider.toUpperCase()}.`);
                }
              }}
              onSyncAnotherEmail={() => {
                setShowEmailSyncStatus(false);
                setShowProviderModal(true);
              }}
              onSyncNow={() => {
                console.log('[WalletScreen] Manual sync triggered');
              }}
            />
          </TripErrorBoundary>

          <MultiProviderSelector
            visible={showProviderModal}
            onClose={() => setShowProviderModal(false)}
            tripId={selectedTripId!}
            currentProvider={inboxProvider}
            onSelectProvider={handleSelectProvider}
            onConnectProvider={handleConnectProvider}
            onDisconnectProvider={handleDisconnectProvider}
            onSyncAll={handleSyncAll}
          />

          <ExpenseForm
            visible={showExpenseForm}
            onClose={() => {
              setShowExpenseForm(false);
              setEditingExpense(null);
            }}
            baseCurrency={selectedTrip.baseCurrency || 'USD'}
            onSubmit={async (expense) => {
              try {
                if (editingExpense) {
                  await handleEditExpense(editingExpense, expense);
                } else {
                  await handleAddExpense(expense);
                }
              } catch (error) {
                console.error('Error saving expense:', error);
                throw error;
              }
            }}
            tripId={selectedTrip.id}
            userId={firebaseUser.uid}
            initialExpense={editingExpense || undefined}
            defaultDate={selectedTrip.startDate}
            tripStartDate={selectedTrip.startDate}
            tripEndDate={selectedTrip.endDate}
            trip={selectedTrip}
          />

          <BudgetEditorModal
            visible={showBudgetEditor}
            trip={selectedTrip}
            onClose={() => setShowBudgetEditor(false)}
            onSave={handleSaveBudget}
          />

          {/* Export Menu */}
          {selectedTrip && (
            <ExportMenu
              visible={showExportMenu}
              onClose={() => setShowExportMenu(false)}
              trip={selectedTrip}
              expenses={expenses}
              bookings={bookings}
              itineraryItems={itineraryItems}
              budgetSnapshot={budgetSnapshot}
            />
          )}
        </>
      )}
    </Screen>
  );
}

// Budget Editor Modal Component
function BudgetEditorModal({
  visible,
  trip,
  onClose,
  onSave,
}: {
  visible: boolean;
  trip: Trip;
  onClose: () => void;
  onSave: (
    budget: number | null,
    currency: string,
    thresholds: number[],
    categoryAllocations?: BudgetCategoryAllocations,
  ) => Promise<void>;
}) {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const modalTopInset = effectiveTopInset(insets.top);
  const { showSuccess, showError } = useToast();
  const [budgetInput, setBudgetInput] = useState('');
  const [budgetCurrency, setBudgetCurrency] = useState<string>(trip.baseCurrency || 'USD');
  const [savingBudget, setSavingBudget] = useState(false);
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const [categoryPercents, setCategoryPercents] = useState<Record<BudgetCategoryKey, number>>({
    flight: 35,
    lodging: 30,
    food: 15,
    transport: 8,
    activity: 7,
    shopping: 3,
    other: 2,
  });
  const [selectedThresholds, setSelectedThresholds] = useState<number[]>(
    trip.budgetAlertThresholds && trip.budgetAlertThresholds.length > 0
      ? [...trip.budgetAlertThresholds].sort((a, b) => a - b)
      : DEFAULT_BUDGET_ALERT_THRESHOLDS,
  );
  const [customThreshold, setCustomThreshold] = useState('');

  const formatPercent = (value: number) => `${Math.round(value * 100)}%`;
  const presetThresholds = [0.5, 0.75, 0.8, 0.85, 0.9, 0.95, 1.0];

  useEffect(() => {
    if (visible) {
      setBudgetInput(trip.budget?.toString() || '');
      setBudgetCurrency(trip.baseCurrency || 'USD');
      const normalized = normalizeBudgetCategoryAllocations(trip.budgetCategoryAllocations);
      // Convert fractions -> percents with rounding; keep last bucket as remainder for stability.
      const keys: BudgetCategoryKey[] = ['flight', 'lodging', 'food', 'transport', 'activity', 'shopping', 'other'];
      const next: Record<BudgetCategoryKey, number> = {
        flight: 35,
        lodging: 30,
        food: 15,
        transport: 8,
        activity: 7,
        shopping: 3,
        other: 2,
      };
      let running = 0;
      for (let i = 0; i < keys.length; i++) {
        const k = keys[i];
        if (i === keys.length - 1) {
          next[k] = Math.max(0, 100 - running);
        } else {
          const pct = Math.round((normalized[k] || 0) * 100);
          next[k] = Math.max(0, Math.min(100, pct));
          running += next[k];
        }
      }
      setCategoryPercents(next);
      setSelectedThresholds(
        trip.budgetAlertThresholds && trip.budgetAlertThresholds.length > 0
          ? [...trip.budgetAlertThresholds].sort((a, b) => a - b)
          : DEFAULT_BUDGET_ALERT_THRESHOLDS,
      );
      setCustomThreshold('');
    }
  }, [visible, trip.budget, trip.baseCurrency, trip.budgetAlertThresholds, trip.budgetCategoryAllocations]);

  useEffect(() => {
    if (!visible) return;
    const showSub = Keyboard.addListener('keyboardDidShow', () => setKeyboardVisible(true));
    const hideSub = Keyboard.addListener('keyboardDidHide', () => setKeyboardVisible(false));
    return () => {
      showSub.remove();
      hideSub.remove();
    };
  }, [visible]);

  const toggleThreshold = (value: number) => {
    setSelectedThresholds((prev) => {
      if (prev.includes(value)) {
        return prev.filter((threshold) => threshold !== value);
      }
      return [...prev, value].sort((a, b) => a - b);
    });
  };

  const handleAddCustomThreshold = () => {
    const trimmed = customThreshold.trim();
    if (!trimmed) return;

    const parsed = parseFloat(trimmed);
    if (Number.isNaN(parsed)) {
      Alert.alert('Invalid value', 'Enter a percentage like 75 or 0.75.');
      return;
    }

    const normalized = parsed > 1 ? parsed / 100 : parsed;
    if (normalized <= 0 || normalized > 1.5) {
      Alert.alert('Out of range', 'Enter a value between 1% and 150%.');
      return;
    }

    setSelectedThresholds((prev) =>
      [...new Set([...prev, normalized])].sort((a, b) => a - b),
    );
    setCustomThreshold('');
  };

  const handleSave = async () => {
    const budgetValue = budgetInput.trim() === '' ? null : parseFloat(budgetInput);
    
    if (budgetValue !== null && (isNaN(budgetValue) || budgetValue < 0)) {
      Alert.alert('Invalid Budget', 'Please enter a valid positive number.');
      return;
    }

    setSavingBudget(true);
    try {
      const thresholds =
        selectedThresholds.length > 0
          ? Array.from(new Set(selectedThresholds))
              .filter((value) => value > 0)
              .sort((a, b) => a - b)
          : DEFAULT_BUDGET_ALERT_THRESHOLDS;
      const allocationsToSave = normalizeBudgetCategoryAllocations(categoryPercents);
      await onSave(budgetValue, budgetCurrency, thresholds, allocationsToSave);
      onClose();
      setBudgetInput('');
      setCustomThreshold('');
      showSuccess('Budget and currency updated successfully');
    } catch (error) {
      console.error('[BudgetEditor] Error updating budget:', error);
      showError('Failed to update budget. Please try again.');
    } finally {
      setSavingBudget(false);
    }
  };


  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}
    >
      <KeyboardAvoidingView
        style={{ flex: 1, backgroundColor: theme.colors.background }}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 8 : 0}
      >
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingHorizontal: theme.spacing(2),
            paddingTop: modalTopInset + theme.spacing(2),
            paddingBottom: theme.spacing(2),
            borderBottomWidth: 1,
            borderBottomColor: theme.colors.borderMuted,
          }}
        >
          <ThemedText variant="h3" style={{ fontSize: 20, fontWeight: '700' }}>
            Set Budget
          </ThemedText>

          <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75) }}>
            {keyboardVisible && (
              <Pressable
                onPress={() => Keyboard.dismiss()}
                style={({ pressed }) => ({
                  paddingHorizontal: theme.spacing(1),
                  paddingVertical: theme.spacing(0.75),
                  borderRadius: theme.radii.md,
                  opacity: pressed ? 0.7 : 1,
                  backgroundColor: theme.colors.borderMuted + '40',
                })}
                accessibilityRole="button"
                accessibilityLabel="Dismiss keyboard"
              >
                <ThemedText variant="caption" style={{ fontWeight: '700' }}>
                  Done
                </ThemedText>
              </Pressable>
            )}

            <Pressable
              onPress={() => {
                Keyboard.dismiss();
                onClose();
              }}
              style={({ pressed }) => ({
                padding: theme.spacing(1),
                borderRadius: theme.radii.md,
                opacity: pressed ? 0.7 : 1,
              })}
              accessibilityRole="button"
              accessibilityLabel="Close budget editor"
            >
              <Ionicons name="close-outline" size={24} color={theme.colors.text} />
            </Pressable>
          </View>
        </View>

        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={{
            padding: theme.spacing(2),
            gap: theme.spacing(2),
            paddingBottom: insets.bottom + theme.spacing(4),
          }}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode={Platform.OS === 'ios' ? 'on-drag' : 'none'}
          showsVerticalScrollIndicator
          scrollEventThrottle={16}
          bounces
          alwaysBounceVertical
        >
            <Caption color={theme.colors.textSecondary}>
              Set your overall budget amount for this trip. This will be automatically allocated across expense categories.
            </Caption>

            <SelectField
              label="Currency"
              placeholder="Select currency"
              value={budgetCurrency}
              onValueChange={setBudgetCurrency}
              options={CURRENCY_OPTIONS}
            />

            <InputField
              label="Total Budget"
              placeholder="Enter budget amount"
              value={budgetInput}
              onChangeText={setBudgetInput}
              keyboardType={Platform.OS === 'ios' ? 'decimal-pad' : 'numeric'}
              returnKeyType="done"
              blurOnSubmit
              onSubmitEditing={() => Keyboard.dismiss()}
              autoFocus
            />

            <Card variant="outlined">
              <View style={{ gap: theme.spacing(1.5) }}>
                <View>
                  <SubHeading style={{ fontSize: 15 }}>Spending category %</SubHeading>
                  <Caption color={theme.colors.textSecondary}>
                    Set how your total budget is allocated across categories. We’ll normalize these to 100% on save.
                  </Caption>
                </View>

                {(() => {
                  const totalPct =
                    Object.values(categoryPercents).reduce((sum, v) => sum + (typeof v === 'number' ? v : 0), 0) || 0;
                  if (totalPct === 100) return null;
                  const isOver = totalPct > 100;
                  return (
                    <View
                      style={{
                        padding: theme.spacing(1.25),
                        borderRadius: theme.radii.md,
                        borderWidth: 1,
                        borderColor: isOver ? theme.colors.error + '55' : theme.colors.warning + '55',
                        backgroundColor: isOver ? theme.colors.error + '10' : theme.colors.warning + '10',
                      }}
                    >
                      <ThemedText variant="bodyStrong" style={{ fontSize: 13 }}>
                        {isOver ? 'Total exceeds 100%' : 'Total is below 100%'}
                      </ThemedText>
                      <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.25) }}>
                        Current total: {Math.round(totalPct)}%.{' '}
                        {isOver
                          ? 'Reduce one or more categories — increases are capped until total is ≤ 100%.'
                          : 'You can allocate the remaining % across categories (or we’ll normalize to 100% on save).'}
                      </Caption>
                    </View>
                  );
                })()}

                {(
                  [
                    { key: 'flight', label: 'Flights' },
                    { key: 'lodging', label: 'Lodging' },
                    { key: 'food', label: 'Food & Dining' },
                    { key: 'transport', label: 'Transport' },
                    { key: 'activity', label: 'Activities' },
                    { key: 'shopping', label: 'Shopping' },
                    { key: 'other', label: 'Other' },
                  ] as Array<{ key: BudgetCategoryKey; label: string }>
                ).map(({ key, label }) => {
                  const value = categoryPercents[key] ?? 0;
                  const total =
                    Object.values(categoryPercents).reduce((sum, v) => sum + (typeof v === 'number' ? v : 0), 0) || 0;
                  // Prevent going above 100%: cap this category by remaining room.
                  // Example: total=103, value=30 => maxForThis=27 (only decreases allowed).
                  const maxForThis = Math.max(0, Math.min(100, 100 - (total - value)));
                  return (
                    <View
                      key={key}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: theme.spacing(1),
                      }}
                    >
                      <View style={{ flex: 1, minWidth: 0 }}>
                        <ThemedText variant="bodyStrong" style={{ fontSize: 13 }}>
                          {label}
                        </ThemedText>
                        <Caption color={theme.colors.textSecondary} style={{ fontSize: 12 }}>
                          {value}% of total
                        </Caption>
                      </View>
                      <NumberStepper
                        value={value}
                        onValueChange={(next) => {
                          const clamped = Math.max(0, Math.min(maxForThis, next));
                          setCategoryPercents((prev) => ({
                            ...prev,
                            [key]: clamped,
                          }));
                        }}
                        min={0}
                        max={maxForThis}
                        step={1}
                        compact
                      />
                    </View>
                  );
                })}

                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
                    Total:{' '}
                    {(
                      Object.values(categoryPercents).reduce((sum, v) => sum + (typeof v === 'number' ? v : 0), 0) || 0
                    ).toFixed(0)}
                    %
                  </Caption>
                  <Pressable
                    onPress={() => {
                      const normalized = normalizeBudgetCategoryAllocations(null);
                      setCategoryPercents({
                        flight: Math.round(normalized.flight * 100),
                        lodging: Math.round(normalized.lodging * 100),
                        food: Math.round(normalized.food * 100),
                        transport: Math.round(normalized.transport * 100),
                        activity: Math.round(normalized.activity * 100),
                        shopping: Math.round(normalized.shopping * 100),
                        other:
                          100 -
                          (Math.round(normalized.flight * 100) +
                            Math.round(normalized.lodging * 100) +
                            Math.round(normalized.food * 100) +
                            Math.round(normalized.transport * 100) +
                            Math.round(normalized.activity * 100) +
                            Math.round(normalized.shopping * 100)),
                      });
                    }}
                    style={({ pressed }) => ({
                      paddingHorizontal: theme.spacing(1),
                      paddingVertical: theme.spacing(0.5),
                      borderRadius: theme.radii.pill,
                      borderWidth: 1,
                      borderColor: theme.colors.borderMuted,
                      opacity: pressed ? 0.7 : 1,
                    })}
                  >
                    <Caption style={{ fontSize: 11, fontWeight: '700' }} color={theme.colors.text}>
                      Reset
                    </Caption>
                  </Pressable>
                </View>
              </View>
            </Card>

            <Card variant="outlined">
              <View style={{ gap: theme.spacing(1.5) }}>
                <View>
                  <SubHeading style={{ fontSize: 15 }}>Budget alerts</SubHeading>
                  <Caption color={theme.colors.textSecondary}>
                    Choose when Tripvio should remind you that you're approaching your budget.
                  </Caption>
                </View>

                <View
                  style={{
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    gap: theme.spacing(1),
                  }}
                >
                  {presetThresholds.map((value) => {
                    const isSelected = selectedThresholds.includes(value);
                    return (
                      <Pressable
                        key={value}
                        onPress={() => toggleThreshold(value)}
                        style={({ pressed }) => ({
                          paddingHorizontal: theme.spacing(1.25),
                          paddingVertical: theme.spacing(0.75),
                          borderRadius: theme.radii.pill,
                          borderWidth: 1,
                          borderColor: isSelected ? theme.colors.primary : theme.colors.borderMuted,
                          backgroundColor: isSelected
                            ? theme.colors.primary + '15'
                            : theme.colors.surface,
                          opacity: pressed ? 0.7 : 1,
                        })}
                      >
                        <ThemedText
                          variant="caption"
                          style={{
                            fontSize: 12,
                            fontWeight: '600',
                            color: isSelected ? theme.colors.primary : theme.colors.textSecondary,
                          }}
                        >
                          {formatPercent(value)}
                        </ThemedText>
                      </Pressable>
                    );
                  })}
                </View>

                <View style={{ flexDirection: 'row', alignItems: 'flex-end', gap: theme.spacing(1) }}>
                  <View style={{ flex: 1 }}>
                    <InputField
                      label="Custom alert (%)"
                      placeholder="e.g. 65"
                      value={customThreshold}
                      onChangeText={setCustomThreshold}
                      keyboardType={Platform.OS === 'ios' ? 'decimal-pad' : 'numeric'}
                      returnKeyType="done"
                      blurOnSubmit
                      onSubmitEditing={() => Keyboard.dismiss()}
                    />
                  </View>
                  <Button
                    label="Add"
                    variant="secondary"
                    onPress={() => {
                      Keyboard.dismiss();
                      handleAddCustomThreshold();
                    }}
                    disabled={!customThreshold.trim()}
                    style={{
                      paddingHorizontal: theme.spacing(1.5),
                      paddingVertical: theme.spacing(0.75),
                    }}
                  />
                </View>

                <Caption color={theme.colors.textSecondary}>
                  Alerts will trigger at:{' '}
                  {(
                    selectedThresholds.length > 0
                      ? [...selectedThresholds].sort((a, b) => a - b)
                      : DEFAULT_BUDGET_ALERT_THRESHOLDS
                  )
                    .map((value) => formatPercent(value))
                    .join(', ')}
                </Caption>
              </View>
            </Card>

            <View style={{ flexDirection: 'row', gap: theme.spacing(1) }}>
              <Button
                label="Save"
                onPress={() => {
                  Keyboard.dismiss();
                  void handleSave();
                }}
                disabled={savingBudget}
                style={{ flex: 1 }}
              />
              <Button
                label="Cancel"
                variant="ghost"
                onPress={() => {
                  Keyboard.dismiss();
                  onClose();
                  setBudgetInput('');
                  setBudgetCurrency(trip.baseCurrency || 'USD');
                }}
                style={{ flex: 1 }}
              />
            </View>
          </ScrollView>
      </KeyboardAvoidingView>
    </Modal>
  );
}

export default function WalletScreen() {
  return (
    <TripErrorBoundary name="WalletScreen">
      <WalletScreenInner />
    </TripErrorBoundary>
  );
}
